import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../firebase/AuthContext";
import { auth } from "../../firebase/firebase";
import { signOut } from "firebase/auth";
import { useAnalytics } from "../../firebase/hooks/useAnalytics";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ChatWidgetV2 } from "../../embed/ChatWidget/chatWidget";

gsap.registerPlugin(ScrollTrigger);

/* -------------------------------------------------------------------------- */
/*  Inline icon set (stroke icons, currentColor)                              */
/* -------------------------------------------------------------------------- */
const Icon = ({ path, className = "w-6 h-6" }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
  >
    {path}
  </svg>
);

const ICONS = {
  chat: (
    <>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
      <path d="M8.5 11.5h.01M12 11.5h.01M15.5 11.5h.01" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="5" rx="8" ry="3" />
      <path d="M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5" />
      <path d="M4 11v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" />
    </>
  ),
  chart: (
    <>
      <path d="M3 3v18h18" />
      <path d="M7 15l3-4 3 3 4-6" />
    </>
  ),
  code: (
    <>
      <path d="M16 18l6-6-6-6" />
      <path d="M8 6l-6 6 6 6" />
    </>
  ),
  video: (
    <>
      <rect x="2.5" y="6" width="14" height="12" rx="2" />
      <path d="M16.5 10l5-3v10l-5-3" />
    </>
  ),
  shield: (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z" />
    </>
  ),
  sparkles: (
    <>
      <path d="M12 3l1.6 4.6L18 9l-4.4 1.4L12 15l-1.6-4.6L6 9l4.4-1.4L12 3z" />
      <path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14z" />
    </>
  ),
  bolt: <path d="M13 2L4.5 13.5H11l-1 8.5L19.5 10H13l0-8z" />,
  plug: (
    <>
      <path d="M9 2v6M15 2v6" />
      <path d="M6 8h12v3a6 6 0 0 1-12 0V8z" />
      <path d="M12 17v5" />
    </>
  ),
  arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
  play: <path d="M6 4l14 8-14 8V4z" />,
  book: (
    <>
      <path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v17H6.5A2.5 2.5 0 0 0 4 21.5v-17z" />
      <path d="M4 21.5A2.5 2.5 0 0 1 6.5 19H20" />
    </>
  ),
};

/* -------------------------------------------------------------------------- */
/*  Localised copy                                                            */
/* -------------------------------------------------------------------------- */
const buildCopy = (isEN) => ({
  nav: {
    signIn: isEN ? "Sign in" : "登入",
    accessPims: isEN ? "Access PIMS" : "進入 PIMS",
    signOut: isEN ? "Sign out" : "登出",
    signedInAs: isEN ? "Signed in as" : "登入身分",
    docs: isEN ? "Docs" : "文件",
  },
  hero: {
    badge: isEN ? "AI-powered product intelligence" : "AI 驅動的產品智慧",
    title1: isEN ? "Turn your product data" : "讓您的產品數據",
    title2: isEN ? "into instant answers." : "化為即時解答。",
    sub: isEN
      ? "T-Global's Product Information Management platform unifies your specs, competitor data and documentation — then lets an AI agent answer every question, on your site or ours."
      : "T-Global 的產品資訊管理平台整合您的規格、競品數據與技術文件，再由 AI 智能代理在您的網站或我們的平台上回答每一個問題。",
    welcome: isEN ? "Welcome back," : "歡迎回來，",
    signInHint: isEN
      ? "Sign in with your T-Global account to continue."
      : "使用您的 T-Global 帳號登入即可繼續。",
    docs: isEN ? "Read the docs" : "閱讀文件",
    trust: isEN
      ? "Built for thermal-material teams that move fast."
      : "為高效運作的散熱材料團隊而打造。",
  },
  chat: {
    title: isEN ? "T-Global Sales Assistant" : "T-Global 智能助理",
    online: isEN ? "Online" : "線上",
    examples: isEN
      ? [
          {
            q: "What's the thermal conductivity of the TG-A series?",
            a: "The TG-A series delivers 3.0–6.0 W/m·K depending on thickness, with stable performance from -40°C to 200°C.",
          },
          {
            q: "Compare our gap filler against the competitor.",
            a: "Our gap filler offers 18% higher conductivity and lower thermal resistance at the same 2.0mm thickness. Want the full table?",
          },
          {
            q: "Show me the datasheet for the 3 W/m·K pad.",
            a: "Here's the datasheet — viscosity, hardness and reliability test results are all included.",
          },
        ]
      : [
          {
            q: "TG-A 系列的導熱係數是多少？",
            a: "TG-A 系列依厚度提供 3.0–6.0 W/m·K，在 -40°C 至 200°C 之間表現穩定。",
          },
          {
            q: "把我們的縫隙填充材與競品比較一下。",
            a: "在相同 2.0mm 厚度下，我們的填充材導熱率高出 18%，熱阻更低。需要完整對照表嗎？",
          },
          {
            q: "給我看 3 W/m·K 導熱片的規格書。",
            a: "這是規格書 — 黏度、硬度與可靠度測試結果都包含在內。",
          },
        ],
  },
  statsHeading: isEN ? "Everything in one place" : "一站式整合",
  stats: isEN
    ? [
        { value: "5+", label: "AI capabilities" },
        { value: "2-line", label: "website install" },
        { value: "EN / 中文", label: "fully bilingual" },
        { value: "24/7", label: "always answering" },
      ]
    : [
        { value: "5+", label: "AI 功能" },
        { value: "2 行", label: "網站嵌入" },
        { value: "中 / EN", label: "完整雙語" },
        { value: "24/7", label: "全天候回應" },
      ],
  featuresHeading: {
    eyebrow: isEN ? "Capabilities" : "核心能力",
    title: isEN ? "A platform, not a demo." : "一個平台，而非一個展示。",
    sub: isEN
      ? "Each piece is already live in the product — connect your data once and every surface gets smarter."
      : "每項功能都已在產品中上線 — 一次連接您的數據，所有環節都更聰明。",
  },
  features: isEN
    ? [
        {
          key: "chat",
          icon: "chat",
          title: "Conversational AI agent",
          desc: "Ask anything about your catalogue in natural language and get sourced, accurate answers in seconds.",
          span: "lg",
        },
        {
          key: "pim",
          icon: "database",
          title: "Product Information Management",
          desc: "Centralise specs, attributes and properties as a single source of truth.",
        },
        {
          key: "competitor",
          icon: "chart",
          title: "Competitor analysis",
          desc: "Benchmark your products against the competition with structured, side-by-side data.",
        },
        {
          key: "embed",
          icon: "code",
          title: "Embed on any website",
          desc: "Drop a script into WordPress or plain HTML — no engineering needed.",
          span: "lg",
        },
        {
          key: "video",
          icon: "video",
          title: "Video Q&A",
          desc: "Query video-based knowledge sources directly inside the chat.",
        },
        {
          key: "roles",
          icon: "shield",
          title: "Role-based access",
          desc: "Granular editor, viewer and admin permissions keep your data safe.",
        },
        {
          key: "lang",
          icon: "globe",
          title: "Bilingual by design",
          desc: "Switch seamlessly between English and 中文 with full localisation.",
        },
      ]
    : [
        {
          key: "chat",
          icon: "chat",
          title: "對話式 AI 代理",
          desc: "用自然語言詢問產品型錄中的任何問題，數秒內取得有依據的精準答案。",
          span: "lg",
        },
        {
          key: "pim",
          icon: "database",
          title: "產品資訊管理",
          desc: "集中管理規格、屬性與特性，建立單一可信來源。",
        },
        {
          key: "competitor",
          icon: "chart",
          title: "競品分析",
          desc: "以結構化的並排數據，將您的產品與競品進行對標。",
        },
        {
          key: "embed",
          icon: "code",
          title: "嵌入任何網站",
          desc: "在 WordPress 或純 HTML 中貼上行程式碼，無需工程支援。",
          span: "lg",
        },
        {
          key: "video",
          icon: "video",
          title: "影片問答",
          desc: "直接在對話中查詢以影片為基礎的知識來源。",
        },
        {
          key: "roles",
          icon: "shield",
          title: "角色權限控管",
          desc: "細緻的編輯者、檢視者與管理員權限，確保數據安全。",
        },
        {
          key: "lang",
          icon: "globe",
          title: "雙語原生設計",
          desc: "在英文與中文之間無縫切換，提供完整在地化。",
        },
      ],
  stepsHeading: {
    eyebrow: isEN ? "How it works" : "運作方式",
    title: isEN ? "Live in three steps." : "三步驟即可上線。",
  },
  steps: isEN
    ? [
        {
          icon: "database",
          title: "Connect your data",
          desc: "Import product specs, documents and video sources into one managed workspace.",
        },
        {
          icon: "sparkles",
          title: "Let the AI learn it",
          desc: "Your agent indexes everything and is ready to answer — no prompt engineering required.",
        },
        {
          icon: "plug",
          title: "Embed & go live",
          desc: "Copy the two-line widget onto your site, or use it inside the dashboard. Done.",
        },
      ]
    : [
        {
          icon: "database",
          title: "連接您的數據",
          desc: "將產品規格、文件與影片來源匯入單一受管理的工作空間。",
        },
        {
          icon: "sparkles",
          title: "讓 AI 學習",
          desc: "您的代理會索引所有內容並準備好回答 — 無需提示工程。",
        },
        {
          icon: "plug",
          title: "嵌入並上線",
          desc: "將兩行程式的小工具複製到您的網站，或在儀表板內使用。完成。",
        },
      ],
  finalCta: {
    title: isEN
      ? "Ready to make your product data answer for itself?"
      : "準備好讓您的產品數據自己開口回答了嗎？",
    sub: isEN
      ? "Sign in and put an AI agent in front of your catalogue today."
      : "立即登入，為您的產品型錄配上 AI 智能代理。",
  },
  footer: isEN
    ? "v1.0.0 · Copyright © 2026 Horuseye Technology. All rights reserved."
    : "v1.0.0 · 版權 © 2026 Horuseye Technology 保留所有權利。",
});

/* -------------------------------------------------------------------------- */
/*  Hero live-chat mockup with typewriter                                     */
/* -------------------------------------------------------------------------- */
function HeroChat({ copy }) {
  const examples = copy.chat.examples;
  const [index, setIndex] = useState(0);
  const [typed, setTyped] = useState("");
  const [phase, setPhase] = useState("typing"); // typing | thinking | answer

  useEffect(() => {
    const current = examples[index];
    let timers = [];

    // typewriter on the question
    setTyped("");
    setPhase("typing");
    let i = 0;
    const typeNext = () => {
      i += 1;
      setTyped(current.q.slice(0, i));
      if (i < current.q.length) {
        timers.push(setTimeout(typeNext, 28 + Math.random() * 40));
      } else {
        timers.push(setTimeout(() => setPhase("thinking"), 350));
        timers.push(setTimeout(() => setPhase("answer"), 1250));
        timers.push(
          setTimeout(() => setIndex((p) => (p + 1) % examples.length), 5200),
        );
      }
    };
    timers.push(setTimeout(typeNext, 400));

    return () => timers.forEach(clearTimeout);
  }, [index, examples]);

  const current = examples[index];

  return (
    <div className="hero-chat relative w-full max-w-md">
      {/* glow */}
      <div className="absolute -inset-6 bg-orange-500/20 blur-3xl rounded-full pointer-events-none" />
      <div className="relative rounded-3xl border border-white/10 bg-[#2a2a2a]/80 backdrop-blur-xl shadow-2xl overflow-hidden">
        {/* header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
          <div className="relative w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white">
            <Icon path={ICONS.sparkles} className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-white">
              {copy.chat.title}
            </p>
            <p className="text-[11px] text-emerald-400 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              {copy.chat.online}
            </p>
          </div>
          {/* <Icon path={ICONS.bolt} className="w-4 h-4 text-orange-400" /> */}
        </div>

        {/* messages */}
        <div className="px-5 py-6 space-y-4 min-h-[260px] flex flex-col justify-end">
          {/* user question */}
          <div className="flex justify-end">
            <div className="max-w-[85%] rounded-2xl rounded-br-md bg-orange-500 text-white text-sm px-4 py-2.5 shadow-lg">
              {typed}
              {phase === "typing" && (
                <span className="inline-block w-0.5 h-4 ml-0.5 align-middle bg-white/80 animate-pulse" />
              )}
            </div>
          </div>

          {/* assistant answer / thinking */}
          {phase !== "typing" && (
            <div className="flex justify-start">
              <div className="max-w-[88%] rounded-2xl rounded-bl-md bg-white/10 border border-white/10 text-gray-100 text-sm px-4 py-3">
                {phase === "thinking" ? (
                  <span className="flex gap-1 py-1">
                    <span className="w-2 h-2 rounded-full bg-orange-400 animate-bounce [animation-delay:-0.3s]" />
                    <span className="w-2 h-2 rounded-full bg-orange-400 animate-bounce [animation-delay:-0.15s]" />
                    <span className="w-2 h-2 rounded-full bg-orange-400 animate-bounce" />
                  </span>
                ) : (
                  <span className="animate-[fadeIn_0.4s_ease-out]">
                    {current.a}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* input bar (decorative) */}
        <div className="px-5 pb-5">
          <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
            <span className="text-sm text-gray-400 flex-1">Ask anything…</span>
            <span className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center text-white">
              <Icon path={ICONS.arrow} className="w-4 h-4" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main landing page                                                        */
/* -------------------------------------------------------------------------- */
export default function Menu() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  // The chat widget gets the signed-in user's token through this, if any.
  const getWidgetToken = useCallback(
    async () => (currentUser ? currentUser.getIdToken() : null),
    [currentUser],
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const rootRef = useRef(null);
  const { trackPageView } = useAnalytics();

  const getLangFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("lang") === "zh" ? "zh" : "en";
  };

  const language = getLangFromUrl();
  const isEN = language === "en";
  const copy = useMemo(() => buildCopy(isEN), [isEN]);

  useEffect(() => {
    trackPageView("Landing/Menu Page");
  }, [trackPageView]);

  // sticky nav state
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close menus on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        !event.target.closest("[data-mobile-menu-trigger]")
      ) {
        setIsMobileMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // website chatbot on this page only: its script adds #glopal-bot-root to
  // <body>, which outlives this page, so a CSS rule hides it whenever the body
  // lacks this page's class — including when it mounts after the visitor left
  useEffect(() => {
    if (!document.getElementById("glopal-bot-visibility")) {
      const style = document.createElement("style");
      style.id = "glopal-bot-visibility";
      style.textContent =
        "body:not(.glopal-bot-page) #glopal-bot-root { display: none; }";
      document.head.appendChild(style);
    }
    document.body.classList.add("glopal-bot-page");
    return () => document.body.classList.remove("glopal-bot-page");
  }, []);

  // GSAP entrance + scroll animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero entrance timeline
      const tl = gsap.timeline({
        defaults: { ease: "power3.out", duration: 0.9 },
      });
      tl.from(".hero-badge", { y: 20, opacity: 0, duration: 0.6 })
        .from(".hero-line", { y: 40, opacity: 0, stagger: 0.12 }, "-=0.2")
        .from(".hero-sub", { y: 24, opacity: 0 }, "-=0.5")
        .from(".hero-cta", { y: 20, opacity: 0, stagger: 0.1 }, "-=0.5")
        .from(
          ".hero-chat",
          { y: 60, opacity: 0, scale: 0.94, duration: 1.1 },
          "-=0.9",
        )
        .from(".hero-trust", { opacity: 0 }, "-=0.4");

      // floating orbs
      gsap.to(".orb-1", {
        y: 40,
        x: 20,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
      gsap.to(".orb-2", {
        y: -50,
        x: -30,
        duration: 10,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      // Resilient scroll reveal. Using ScrollTrigger.batch (instead of a
      // `gsap.from` per element) so that elements already in view on load
      // are animated in too, and a misfiring trigger can never leave the
      // content permanently stuck at opacity:0. `once` keeps them visible
      // for good once revealed.
      const reveal = (
        selector,
        { y = 40, start = "top 88%", stagger = 0.08 } = {},
      ) => {
        const els = gsap.utils.toArray(selector);
        if (!els.length) return;
        gsap.set(els, { opacity: 0, y });
        ScrollTrigger.batch(els, {
          start,
          once: true,
          onEnter: (batch) =>
            gsap.to(batch, {
              opacity: 1,
              y: 0,
              duration: 0.7,
              stagger,
              ease: "power2.out",
              overwrite: true,
            }),
        });
      };

      reveal(".stat-card", { y: 30, start: "top 92%" });
      reveal(".reveal-up", { y: 36, start: "top 88%", stagger: 0 });
      reveal(".feature-card", { y: 44, start: "top 88%", stagger: 0.08 });
      reveal(".step-card", { y: 40, start: "top 88%", stagger: 0.12 });

      // Recalculate trigger positions after the chat widget, images and
      // fonts settle — this is what was throwing off the start points.
      ScrollTrigger.refresh();
    }, rootRef);

    // One more refresh once everything (incl. async assets) has loaded, and
    // a hard fail-safe that guarantees nothing stays hidden if a trigger
    // somehow never fires.
    const onLoad = () => ScrollTrigger.refresh();
    window.addEventListener("load", onLoad);
    const failSafe = setTimeout(() => {
      gsap.utils
        .toArray(".stat-card, .reveal-up, .feature-card, .step-card")
        .forEach((el) => {
          if (Number(gsap.getProperty(el, "opacity")) === 0) {
            gsap.set(el, { opacity: 1, y: 0 });
          }
        });
    }, 3000);

    return () => {
      window.removeEventListener("load", onLoad);
      clearTimeout(failSafe);
      ctx.revert();
    };
  }, [isEN]);

  const setLanguageInUrl = (lang) => {
    const params = new URLSearchParams(location.search);
    params.set("lang", lang);
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleLoginClick = () =>
    navigate({ pathname: "/login", search: `?lang=${language}` });

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleGetStarted = () => {
    navigate({
      pathname: currentUser ? "/tg-product-assistant" : "/login",
      search: `?lang=${language}`,
    });
  };

  const goDocs = () =>
    navigate({ pathname: "/docs", search: `?lang=${language}` });

  const goDashboard = () =>
    navigate({ pathname: "/tg-product-assistant", search: `?lang=${language}` });

  return (
    <div
      ref={rootRef}
      className="relative min-h-screen overflow-x-hidden bg-[#2b2b2b] text-white antialiased selection:bg-orange-500/30"
    >
      {/* <ChatWidgetV2
                className="fixed z-50 right-6 bottom-6"
                getToken={getWidgetToken}
                userId={currentUser?.uid}
            /> */}

      <script
        src="https://chandini-project.web.app/glopal-bot.js"
        data-bot-id="fde9fb64-7ddc-4c6f-b706-63092048e666"
        async
      ></script>

      {/* ── Background layers ─────────────────────────────────── */}
      <div className="pointer-events-none fixed inset-0 z-0">
        {/* grid */}
        {/* <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_at_top,black,transparent_75%)]" /> */}
        {/* orbs */}
        <div className="orb-1 absolute -top-32 -left-24 w-[28rem] h-[28rem] rounded-full bg-orange-500/20 blur-[120px]" />
        <div className="orb-2 absolute top-1/3 -right-24 w-[32rem] h-[32rem] rounded-full bg-orange-600/10 blur-[140px]" />
        {/* vignette */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#1f1f1f]" />
      </div>

      {/* ── Navigation ────────────────────────────────────────── */}
      <nav
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
          scrolled
            ? "bg-[#2b2b2b]/80 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3.5 flex items-center justify-between">
          {/* logo */}
          <div className="flex items-center gap-3">
            <img
              src="/logo_tglobal_2.png"
              alt="T-Global"
              className="h-8 md:h-9 w-auto"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/logo_tglobal.png";
              }}
            />
          </div>

          {/* desktop right */}
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={goDocs}
              className="text-sm font-medium text-gray-300 hover:text-white transition-colors px-3 py-2"
            >
              {copy.nav.docs}
            </button>

            {/* language */}
            <div className="flex items-center bg-white/5 border border-white/10 rounded-lg p-1">
              {["en", "zh"].map((lng) => (
                <button
                  key={lng}
                  onClick={() => setLanguageInUrl(lng)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                    language === lng
                      ? "bg-orange-500 text-white shadow-sm"
                      : "text-gray-400 hover:text-white"
                  }`}
                >
                  {lng === "en" ? "EN" : "中文"}
                </button>
              ))}
            </div>

            {currentUser && (
              <button
                onClick={goDashboard}
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-lg transition-colors text-sm font-semibold shadow-lg shadow-orange-500/20"
              >
                {/* <Icon path={ICONS.bolt} className="w-4 h-4" /> */}
                {copy.nav.accessPims}
              </button>
            )}

            {currentUser ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 transition-colors"
                >
                  <div className="w-7 h-7 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-semibold">
                      {currentUser.email?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden lg:block text-sm font-medium text-gray-200">
                    {currentUser.email?.split("@")[0]}
                  </span>
                  <Icon
                    path={<path d="M19 9l-7 7-7-7" />}
                    className={`w-4 h-4 text-gray-400 transition-transform ${
                      isDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-[#333] rounded-xl shadow-2xl border border-white/10 py-1.5 animate-[scaleIn_0.15s_ease-out] origin-top-right">
                    <div className="px-4 py-2.5 border-b border-white/10">
                      <p className="text-xs text-gray-400">
                        {copy.nav.signedInAs}
                      </p>
                      <p className="text-sm text-gray-100 truncate">
                        {currentUser.email}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        navigate({
                          pathname: "/tg-product-assistant",
                          search: `?lang=${language}`,
                        });
                        setIsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-200 hover:bg-white/5 flex items-center gap-2.5"
                    >
                      <Icon
                        path={ICONS.bolt}
                        className="w-4 h-4 text-orange-400"
                      />
                      {copy.nav.accessPims}
                    </button>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2.5"
                    >
                      <Icon
                        path={
                          <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        }
                        className="w-4 h-4"
                      />
                      {copy.nav.signOut}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleLoginClick}
                className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-lg transition-colors text-sm font-semibold shadow-lg shadow-orange-500/20"
              >
                {copy.nav.signIn}
              </button>
            )}
          </div>

          {/* mobile trigger */}
          <div className="md:hidden flex items-center gap-2">
            {currentUser && (
              <button
                onClick={goDashboard}
                className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold"
              >
                {copy.nav.accessPims}
              </button>
            )}
            {currentUser && (
              <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-semibold">
                  {currentUser.email?.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-mobile-menu-trigger="true"
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <Icon
                path={
                  isMobileMenuOpen ? (
                    <path d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path d="M4 6h16M4 12h16M4 18h16" />
                  )
                }
                className="w-6 h-6 text-gray-200"
              />
            </button>
          </div>
        </div>

        {/* mobile menu */}
        {isMobileMenuOpen && (
          <div
            ref={mobileMenuRef}
            className="md:hidden mx-4 mb-4 rounded-2xl border border-white/10 bg-[#333]/95 backdrop-blur-xl p-4 animate-[slideUp_0.25s_ease-out]"
          >
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  goDocs();
                  setIsMobileMenuOpen(false);
                }}
                className="text-left px-4 py-3 rounded-lg text-gray-200 hover:bg-white/5 font-medium"
              >
                {copy.nav.docs}
              </button>

              <div className="flex items-center justify-center">
                <div className="flex items-center bg-white/5 border border-white/10 rounded-lg p-1">
                  {["en", "zh"].map((lng) => (
                    <button
                      key={lng}
                      onClick={(e) => {
                        e.stopPropagation();
                        setLanguageInUrl(lng);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`px-4 py-2 rounded-md text-sm font-medium ${
                        language === lng
                          ? "bg-orange-500 text-white"
                          : "text-gray-400"
                      }`}
                    >
                      {lng === "en" ? "EN" : "中文"}
                    </button>
                  ))}
                </div>
              </div>

              {currentUser ? (
                <>
                  <div className="px-4 py-2.5 bg-white/5 rounded-lg">
                    <p className="text-xs text-gray-400">
                      {copy.nav.signedInAs}
                    </p>
                    <p className="text-sm text-gray-100 truncate">
                      {currentUser.email}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate({
                        pathname: "/tg-product-assistant",
                        search: `?lang=${language}`,
                      });
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-lg font-semibold"
                  >
                    {copy.nav.accessPims}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full border border-red-400/40 text-red-400 hover:bg-red-500/10 px-4 py-3 rounded-lg font-semibold"
                  >
                    {copy.nav.signOut}
                  </button>
                </>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLoginClick();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-lg font-semibold"
                >
                  {copy.nav.signIn}
                </button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* ── Hero ──────────────────────────────────────────────── */}
      <header className="relative z-10 max-w-7xl mx-auto px-6 pt-36 pb-20 md:pt-44 md:pb-28">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            {/* <div className="hero-badge inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-1.5 text-sm text-orange-300 mb-7">
                            <Icon
                                path={ICONS.sparkles}
                                className="w-4 h-4"
                            />
                            {copy.hero.badge}
                        </div> */}

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.05] tracking-tight">
              <span className="hero-line block">{copy.hero.title1}</span>
              <span className="hero-line block bg-gradient-to-r from-orange-400 via-orange-500 to-amber-400 bg-clip-text text-transparent">
                {copy.hero.title2}
              </span>
            </h1>

            <p className="hero-sub mt-6 text-lg text-gray-300 leading-relaxed max-w-xl">
              {copy.hero.sub}
            </p>

            <div className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4">
              {/* The way into PIMS — a soft pulsing glow keeps it the first
                  thing the eye lands on, signed in or not. */}
              <div className="hero-cta relative">
                <span className="pointer-events-none absolute -inset-1.5 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-400 opacity-60 blur-lg animate-pulse" />
                <button
                  onClick={handleGetStarted}
                  className="group relative w-full inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-9 py-4 rounded-xl text-lg font-bold shadow-xl shadow-orange-500/30 ring-1 ring-white/20 transition-all hover:scale-[1.03]"
                >
                  {copy.nav.accessPims}
                  <Icon
                    path={ICONS.arrow}
                    className="w-5 h-5 transition-transform group-hover:translate-x-1"
                  />
                </button>
              </div>
              {/* GSAP animates the wrapper, not the button: the button's
                  `transition-all` fights the entrance tween and can leave it
                  stuck invisible. */}
              <div className="hero-cta">
                <button
                  onClick={goDocs}
                  className="w-full inline-flex items-center justify-center gap-2 border border-white/15 bg-white/5 hover:bg-white/10 backdrop-blur-sm text-white px-7 py-4 rounded-xl text-base font-semibold transition-all hover:scale-[1.03]"
                >
                  <Icon path={ICONS.book} className="w-5 h-5" />
                  {copy.hero.docs}
                </button>
              </div>
            </div>

            <p className="hero-cta mt-4 text-sm text-gray-400">
              {currentUser ? (
                <>
                  {copy.hero.welcome}{" "}
                  <span className="font-semibold text-white">
                    {currentUser.displayName ||
                      currentUser.email?.split("@")[0]}
                  </span>
                </>
              ) : (
                copy.hero.signInHint
              )}
            </p>

            <p className="hero-trust mt-8 text-sm text-gray-500">
              {copy.hero.trust}
            </p>
          </div>

          {/* live chat mockup */}
          <div className="flex justify-center lg:justify-end">
            <HeroChat copy={copy} />
          </div>
        </div>
      </header>

      {/* ── Stats ─────────────────────────────────────────────── */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {copy.stats.map((s) => (
            <div
              key={s.label}
              className="stat-card rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm px-6 py-7 text-center"
            >
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-br from-orange-300 to-orange-500 bg-clip-text text-transparent">
                {s.value}
              </div>
              <div className="mt-2 text-sm text-gray-400">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features bento ────────────────────────────────────── */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-20 md:py-28">
        <div className="max-w-2xl mb-14">
          <p className="reveal-up text-orange-400 font-semibold text-sm uppercase tracking-wider mb-3">
            {copy.featuresHeading.eyebrow}
          </p>
          <h2 className="reveal-up text-3xl md:text-5xl font-bold tracking-tight">
            {copy.featuresHeading.title}
          </h2>
          <p className="reveal-up mt-4 text-lg text-gray-400">
            {copy.featuresHeading.sub}
          </p>
        </div>

        <div className="feature-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-fr">
          {copy.features.map((f) => (
            <div
              key={f.key}
              className={`feature-card group relative rounded-3xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] backdrop-blur-sm p-7 transition-all duration-300 hover:border-orange-500/40 hover:-translate-y-1 ${
                f.span === "lg" ? "md:col-span-2 lg:col-span-2" : ""
              }`}
            >
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-orange-500/0 to-orange-500/0 group-hover:from-orange-500/[0.06] group-hover:to-transparent transition-all duration-300 pointer-events-none" />
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500/20 to-orange-500/5 border border-orange-500/20 flex items-center justify-center text-orange-400 mb-5 group-hover:scale-110 transition-transform">
                  <Icon path={ICONS[f.icon]} className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {f.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">{f.desc}</p>

                {f.key === "embed" && (
                  <div className="mt-5 rounded-xl bg-black/40 border border-white/10 px-4 py-3 font-mono text-xs text-gray-400 overflow-x-auto">
                    <span className="text-gray-600">&lt;script</span>{" "}
                    <span className="text-orange-300">src</span>=
                    <span className="text-emerald-300">
                      "tglobal-webchatbot.js"
                    </span>
                    <span className="text-gray-600">&gt;&lt;/script&gt;</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────── */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 py-20 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="reveal-up text-orange-400 font-semibold text-sm uppercase tracking-wider mb-3">
            {copy.stepsHeading.eyebrow}
          </p>
          <h2 className="reveal-up text-3xl md:text-5xl font-bold tracking-tight">
            {copy.stepsHeading.title}
          </h2>
        </div>

        <div className="steps-grid relative grid md:grid-cols-3 gap-6">
          {/* connecting line */}
          <div className="hidden md:block absolute top-9 left-[16%] right-[16%] h-px bg-gradient-to-r from-orange-500/0 via-orange-500/40 to-orange-500/0" />
          {copy.steps.map((s, i) => (
            <div key={s.title} className="step-card relative text-center">
              <div className="relative z-10 mx-auto w-16 h-16 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-[#2b2b2b] border border-orange-500/30 flex items-center justify-center text-orange-400 shadow-lg shadow-orange-500/10">
                  <Icon path={ICONS[s.icon]} className="w-7 h-7" />
                </div>
                <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center">
                  {i + 1}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {s.title}
              </h3>
              <p className="text-gray-400 leading-relaxed max-w-xs mx-auto">
                {s.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────── */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 py-20">
        <div className="reveal-up relative overflow-hidden rounded-[2rem] border border-orange-500/20 bg-gradient-to-br from-[#3a3128] via-[#2b2b2b] to-[#2b2b2b] px-8 py-16 md:px-16 md:py-20 text-center">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-96 bg-orange-500/20 blur-[120px] rounded-full pointer-events-none" />
          <div className="relative">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight max-w-2xl mx-auto">
              {copy.finalCta.title}
            </h2>
            <p className="mt-4 text-lg text-gray-300">{copy.finalCta.sub}</p>
            <button
              onClick={handleGetStarted}
              className="group mt-9 inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-4 rounded-xl text-base font-semibold shadow-xl shadow-orange-500/25 transition-all hover:scale-[1.04]"
            >
              {copy.nav.accessPims}
              <Icon
                path={ICONS.arrow}
                className="w-5 h-5 transition-transform group-hover:translate-x-1"
              />
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────── */}
      <footer className="relative z-10 border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <img
            src="/logo_tglobal_2.png"
            alt="T-Global"
            className="h-7 w-auto opacity-70"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/logo_tglobal.png";
            }}
          />
          <p className="text-gray-500 text-xs">{copy.footer}</p>
        </div>
      </footer>
    </div>
  );
}
